import { useEffect, useState } from "react";
import { useLocation, useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft,
  Check,
  X,
  Fuel,
  Cog,
  Calendar,
  Tag,
  RefreshCcw,
  ExternalLink,
  GitCompareArrows,
  LogIn,
  User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import LeadCapture from "@/components/LeadCapture";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK = "https://images.unsplash.com/photo-1624880056139-d1212d7ff347?w=1200&q=80";

function portalUrls(brand, model) {
  const q = encodeURIComponent(`${brand} ${model}`);
  return {
    autoscout: `https://www.autoscout24.it/lst?atype=C&sort=standard&desc=0&ustate=N%2CU&size=20&page=1&cy=I&q=${q}`,
    subito: `https://www.subito.it/annunci-italia/vendita/auto/?q=${q}`,
  };
}

export default function Results() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(location.state || null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]); // indexes selected for compare

  useEffect(() => {
    if (data) return;
    axios
      .get(`${API}/recommend/${id}`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch((e) => setError(e?.response?.data?.detail || "Impossibile caricare i risultati"));
  }, [id, data]);

  const toggleSelect = (i) => {
    setSelected((prev) => {
      if (prev.includes(i)) return prev.filter((x) => x !== i);
      if (prev.length >= 2) {
        toast.info("Puoi confrontare al massimo 2 auto");
        return prev;
      }
      return [...prev, i];
    });
  };

  const openCompare = () => {
    if (selected.length !== 2) {
      toast.error("Seleziona esattamente 2 auto per confrontarle");
      return;
    }
    const cars = selected.map((i) => data.recommendations[i]);
    navigate("/compare", { state: { cars, resultId: id } });
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-lg text-[#666]" data-testid="results-error">{String(error)}</p>
          <Link to="/wizard" className="mt-6 inline-block underline text-[#0055FF]">Ricomincia</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="results-loading">
        <p className="text-[#666]">Caricamento risultati…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="results-page">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl font-semibold tracking-tight" data-testid="results-brand-link">
          auto<span className="text-[#0055FF]">.</span>advisor
        </Link>
        <div className="flex items-center gap-4">
          {user ? (
            <>
              <Link to="/history" className="text-sm font-medium text-[#111] hover:text-[#0055FF]" data-testid="header-history-link">
                Le mie ricerche
              </Link>
              <button
                onClick={logout}
                className="inline-flex items-center gap-2 text-sm font-medium text-[#666] hover:text-[#111]"
                data-testid="header-logout-button"
              >
                <UserIcon className="w-4 h-4" /> Esci
              </button>
            </>
          ) : (
            <Link to="/login" className="inline-flex items-center gap-2 text-sm font-medium text-[#111] hover:text-[#0055FF]" data-testid="header-login-link">
              <LogIn className="w-4 h-4" /> Accedi
            </Link>
          )}
          <Link to="/wizard" className="inline-flex items-center gap-2 text-sm font-medium text-[#111] hover:text-[#0055FF]" data-testid="results-restart-link">
            <RefreshCcw className="w-4 h-4" /> Ricomincia
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pt-6 pb-24">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <span className="inline-block text-xs uppercase tracking-widest text-[#0055FF] font-medium">
            Le nostre proposte
          </span>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
            {data.recommendations.length} auto scelte per te
          </h1>
          {data.intro && (
            <p className="mt-5 text-lg text-[#666] max-w-2xl leading-relaxed" data-testid="results-intro">
              {data.intro}
            </p>
          )}
          <p className="mt-4 text-sm text-[#666]">
            Seleziona 2 auto e confrontale affiancate.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-8">
          {data.recommendations.map((car, i) => (
            <CarCard
              key={`${car.brand}-${car.model}-${i}`}
              car={car}
              index={i}
              selected={selected.includes(i)}
              onToggleSelect={() => toggleSelect(i)}
            />
          ))}
        </div>

        <div className="mt-16 flex flex-col sm:flex-row justify-center gap-4">
          <Link
            to="/wizard"
            className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full border border-[#111] text-[#111] hover:bg-[#111] hover:text-white transition"
            data-testid="results-bottom-restart"
          >
            <ArrowLeft className="w-4 h-4" /> Nuova ricerca
          </Link>
        </div>

        <LeadCapture recommendationId={id} />
      </main>

      {/* Sticky compare CTA */}
      {selected.length > 0 && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-6 inset-x-0 flex justify-center z-40 pointer-events-none"
        >
          <div className="pointer-events-auto flex items-center gap-4 bg-[#111] text-white rounded-full pl-5 pr-2 py-2 shadow-lg" data-testid="compare-bar">
            <span className="text-sm">{selected.length}/2 auto selezionate</span>
            <button
              onClick={openCompare}
              disabled={selected.length !== 2}
              className="inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#0044dd] transition disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium h-10 px-5 rounded-full"
              data-testid="compare-open-button"
            >
              <GitCompareArrows className="w-4 h-4" /> Confronta
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function CarCard({ car, index, selected, onToggleSelect }) {
  const { autoscout, subito } = portalUrls(car.brand, car.model);
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      className={`rounded-3xl overflow-hidden border bg-white grid grid-cols-1 md:grid-cols-5 transition ${
        selected ? "border-[#0055FF] ring-1 ring-[#0055FF]" : "border-[#EAEAEA] hover:shadow-lg"
      }`}
      data-testid={`car-card-${index}`}
    >
      <div className="md:col-span-2 relative bg-[#F2F4F8]">
        <div className="aspect-[16/9] md:aspect-auto md:h-full w-full flex items-center justify-center">
          <img
            src={car.image_url || FALLBACK}
            alt={`${car.brand} ${car.model}`}
            onError={(e) => { e.currentTarget.src = FALLBACK; }}
            className="w-full h-full object-contain p-4"
            data-testid={`car-image-${index}`}
          />
        </div>
        <span className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-[#111]">
          #{index + 1}
        </span>
        <button
          onClick={onToggleSelect}
          className={`absolute top-4 right-4 h-9 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition ${
            selected ? "bg-[#0055FF] text-white" : "bg-white/90 text-[#111] hover:bg-white"
          }`}
          data-testid={`car-select-${index}`}
        >
          <GitCompareArrows className="w-3.5 h-3.5" />
          {selected ? "Selezionata" : "Confronta"}
        </button>
      </div>

      <div className="md:col-span-3 p-6 md:p-8 flex flex-col">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="text-sm text-[#666]" data-testid={`car-brand-${index}`}>{car.brand}</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight" data-testid={`car-model-${index}`}>
              {car.model}
            </h2>
          </div>
          <span className="font-heading text-lg font-medium text-[#0055FF]" data-testid={`car-price-${index}`}>
            {car.price_range_eur}
          </span>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {car.category && <Chip label={car.category.replace(/_/g, " ")} icon={Tag} />}
          {car.fuel_type && <Chip label={car.fuel_type} icon={Fuel} />}
          {car.transmission && <Chip label={car.transmission} icon={Cog} />}
          {car.year_range && <Chip label={car.year_range} icon={Calendar} />}
          {car.horsepower && <Chip label={car.horsepower} icon={Cog} />}
        </div>

        <p className="mt-5 text-[15px] leading-relaxed text-[#333]" data-testid={`car-reason-${index}`}>
          {car.why_recommended}
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {car.pros?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-widest text-[#666] font-medium mb-2">Pro</p>
              <ul className="space-y-1.5">
                {car.pros.map((p, k) => (
                  <li key={k} className="flex items-start gap-2 text-sm text-[#111]">
                    <Check className="w-4 h-4 mt-0.5 text-[#0055FF] shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {car.cons?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-widest text-[#666] font-medium mb-2">Contro</p>
              <ul className="space-y-1.5">
                {car.cons.map((p, k) => (
                  <li key={k} className="flex items-start gap-2 text-sm text-[#111]">
                    <X className="w-4 h-4 mt-0.5 text-[#666] shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="mt-6 pt-5 border-t border-[#EAEAEA] flex flex-wrap gap-3">
          <a
            href={autoscout}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#F2F4F8] hover:bg-[#E5EEFF] text-sm font-medium text-[#111] transition"
            data-testid={`car-autoscout-${index}`}
          >
            Cerca su AutoScout24 <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <a
            href={subito}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#F2F4F8] hover:bg-[#E5EEFF] text-sm font-medium text-[#111] transition"
            data-testid={`car-subito-${index}`}
          >
            Cerca su Subito.it <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </motion.article>
  );
}

function Chip({ label, icon: Icon }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F2F4F8] text-xs font-medium text-[#111] capitalize">
      {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
      {label}
    </span>
  );
}
